import {
  FC,
  useEffect,
  useCallback,
  useState,
  useMemo,
  useContext,
  useRef,
} from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  Separator,
  Input,
  SearchIcon,
  NumberInput,
  Tabs,
  Label,
  H2,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import {
  CalculatorCash,
  ClientSearch,
  HostsField,
  PromoCodeField,
  ShoppingCart,
} from 'components';
import { useIsFeatureAvailable } from 'features';
import {
  usePayment,
  useShoppingCart,
  useEntitySearch,
  ISendingPaymentItem,
  TFilterClientsFn,
} from 'hooks';
import { LocalizationContext } from 'localization';
import { isSandbox } from 'sandbox';
import {
  ONBOARDING_SESSION_CREATED_EVENT,
  ONBOARDING_STEP_CHANGED_EVENT,
  OnboardingSessionCreatedDetail,
  OnboardingStepChangedDetail,
} from 'sandbox/onboarding/events';
import { ONBOARDING_ONE_HOUR_TARIFF_TITLE } from 'sandbox/onboarding/steps';
import {
  BOOKING_FORM_DATA,
  BookingDataResponse,
  GET_BOOKING,
  IGetBookingRequest,
  IGetBookingResponse,
} from 'schemas';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import {
  EPaymentMethod,
  ETariffType,
  EShopItem,
  BookingHost,
  EPaymentItemType,
} from 'types';
import {
  checkShellVersion,
  getActiveSeance,
  getCartItems,
  getPaymentMethod,
  getShoppingCartTotalPrice,
  getTariffsDuration,
} from 'utils';

import { IAvailableItems } from '../../pages/PromoCodeManager/interfaces';
import { DEFAULT_FORM_VALUES } from './constants';
import { DiscountsField } from './DiscountsField';
import { IStartSeanceModalProps } from './interfaces';
import s from './StartSeanceModal.module.scss';
import { Timer } from './Timer';
import { IFormFields } from './types';
import { useGetHostDataForSeans } from './useGetHostDataForSeans';
import {
  getNearestBookingTime,
  getShopItemConfig,
  getShopTabs,
  mergeTariffItems,
} from './utils';

export const StartSeanceModal: FC<IStartSeanceModalProps> = ({
  hostIds,
  onClose,
  clientsUUIDsWithSession,
  setIsDepositOpen,
  setIsDefaultClient,
  bookingId,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, restrictVirtualHostDepositPayment },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data: bookingForEdit } = useQuery<
    IGetBookingResponse,
    IGetBookingRequest
  >(GET_BOOKING, {
    skip: !bookingId || !permissions?.has('booking_manage'),
    variables: { id: bookingId as number },
    fetchPolicy: 'no-cache',
  });

  const { data: bookingFormData, loading: isBookingFormDataLoading } =
    useQuery<BookingDataResponse>(BOOKING_FORM_DATA, {
      fetchPolicy: 'no-cache',
      skip: !permissions?.has('booking_manage') || hostIds.length === 1,
    });

  const [selectedHostsIds, setSelectedHostsIds] = useState<number[]>(hostIds);
  const [availableItems, setAvailableItems] = useState<IAvailableItems[]>([]);

  const currentHosts: Array<BookingHost> =
    bookingFormData?.hostsOverview?.filter((host) =>
      selectedHostsIds.includes(host.id)
    ) || [];

  // ВРЕМЕННОЕ РЕШЕНИЕ ПОКА В TARIFFGRID НЕ ДОБАВЯТ МАССИВ ХОСТОВ
  const hostId = useMemo(() => {
    if (currentHosts?.some((host) => host.bookings?.length)) {
      const nearestBookingTime = getNearestBookingTime(
        currentHosts,
        selectedHostsIds,
        true
      );

      return nearestBookingTime as number;
    }

    return hostIds[0];
  }, [selectedHostsIds, currentHosts, hostIds]);

  const host = useGetHostDataForSeans(hostId);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<IFormFields>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const discount = watch('discount');
  const promoCode = watch('promoCode');
  const clientUUID = watch('clientUUID');
  const method = watch('method');

  const [activeOnboardingAction, setActiveOnboardingAction] = useState<
    string | undefined
  >(() => document.body.dataset.sandboxOnboardingAction);

  useEffect(() => {
    if (!isSandbox) return;

    const handleStepChanged = (event: Event): void => {
      setActiveOnboardingAction(
        (event as CustomEvent<OnboardingStepChangedDetail>).detail.action
      );
    };

    window.addEventListener(ONBOARDING_STEP_CHANGED_EVENT, handleStepChanged);
    return () =>
      window.removeEventListener(
        ONBOARDING_STEP_CHANGED_EVENT,
        handleStepChanged
      );
  }, []);

  const isOnboardingPaymentLocked =
    isSandbox &&
    [
      'open-session-sale',
      'select-one-hour-tariff',
      'select-cash-payment',
    ].includes(activeOnboardingAction ?? '');

  const [disabledButton, setDisabledButton] = useState<boolean>(false);
  const [isRoleClient] = useState<string | undefined>('');

  const finishedAt = useMemo<undefined | string>(
    () => host?.client_sessions?.[0]?.finished_at,
    [host]
  );

  useEffect(() => {
    if (!finishedAt) {
      return;
    } else {
      const dateFinish = dayjs(finishedAt);
      const dateNow = dayjs();
      const diffSeconds = dateFinish.diff(dateNow, 'seconds');

      if (diffSeconds > 5) {
        setTimeout(() => setDisabledButton(true), (diffSeconds - 5) * 1000);
        setTimeout(() => setDisabledButton(false), (diffSeconds + 5) * 1000);
      } else if (diffSeconds >= 1 && diffSeconds <= 5) {
        setDisabledButton(true);
        setTimeout(() => setDisabledButton(false), (diffSeconds + 5) * 1000);
      }
    }
  }, [finishedAt]);

  const {
    combo,
    items,
    addItem,
    setItemAmount,
    clearShoppingCart,
    removeItem,
    setCombo,
  } = useShoppingCart(clientUUID);

  const hasOnboardingTariff = items.some(
    (item) =>
      item.type === EPaymentItemType.TARIFF &&
      item.original.title === ONBOARDING_ONE_HOUR_TARIFF_TITLE
  );

  const disabledCombo = useMemo<boolean>(
    () =>
      !!items.length ||
      !!combo ||
      items.some(
        (item) =>
          item.type === 'TARIFF' && item.original?.disable_sale_receipt_printing
      ),
    [combo, items]
  );

  const disabledTariff = useMemo<boolean>(
    () =>
      items.some(
        (item) =>
          item.type === 'TARIFF' && item.original?.disable_sale_receipt_printing
      ),
    [items]
  );

  const SHOP_ITEM_CONFIG = getShopItemConfig();

  const isComboAvailable = useIsFeatureAvailable('combos');

  const TABS = useMemo(
    () => getShopTabs(t, isComboAvailable),
    [t, isComboAvailable]
  );

  const [tab, setTab] = useState<EShopItem>(EShopItem.TARIFF);
  const activeDiscount = useMemo(() => {
    if (promoCode?.value.type === 'DISCOUNT') {
      return promoCode.value.value;
    }
    if (discount?.value) {
      return discount.value;
    }

    return null;
  }, [discount, promoCode]);

  useEffect(() => {
    setValue('method', null, { shouldValidate: true });
  }, [items]);

  useEffect(() => {
    if (host?.user) {
      setValue('clientUUID', host?.user.uuid);
    }
  }, [host]);

  useEffect(() => {
    clearShoppingCart();
  }, [clientUUID]);

  const { createPayment } = usePayment();

  const filterClientsFn = useCallback<TFilterClientsFn>(
    (client) => !clientsUUIDsWithSession.includes(client.uuid),
    [clientsUUIDsWithSession]
  );

  const isCurrentTariffPausable = useMemo<boolean>(
    () =>
      getActiveSeance(host?.client_sessions[0]?.seances ?? [])?.tariff.kind ===
      ETariffType.PAUSABLE,
    [host]
  );
  const currentSessionEndTime = useMemo<string | null | undefined>(
    () =>
      isCurrentTariffPausable ||
      getActiveSeance(host?.client_sessions[0]?.seances ?? [])?.tariff.kind ===
        ETariffType.PER_MINUTE
        ? null
        : host?.client_sessions[0]?.finished_at,
    [isCurrentTariffPausable, host]
  );

  const { ShopItemsTable } = SHOP_ITEM_CONFIG[tab];

  const total = getShoppingCartTotalPrice(
    items,
    activeDiscount,
    availableItems,
    selectedHostsIds
  );

  const shoppingCartTariffs = items.filter((item) => item.type === 'TARIFF');

  const tariffsDuration = useMemo(() => {
    return getTariffsDuration(shoppingCartTariffs, currentSessionEndTime);
  }, [shoppingCartTariffs.length, currentSessionEndTime, host]);

  const searchInputProps = useEntitySearch({
    addItem,
    client_uuid: clientUUID ?? undefined,
    host_group_id: host?.group_id,
    shift: tariffsDuration,
  });

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!promoCode?.id) {
      setAvailableItems([]);
    }
  }, [promoCode]);

  useEffect(() => {
    reset(({ clientUUID, discount, promoCode }) => ({
      ...DEFAULT_FORM_VALUES,
      clientUUID,
      discount,
      promoCode,
    }));

    // Фокус с задержкой для гарантии, что DOM обновился
    const timeoutId = setTimeout(() => {
      if (searchInputProps.ref.current) {
        searchInputProps.ref.current.focus();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [tab]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const timeoutId = setTimeout(() => {
        if (searchInputProps.ref.current) {
          searchInputProps.ref.current.focus();
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchInputProps.ref]);

  const disableSellByDeposit = restrictVirtualHostDepositPayment
    ? !checkShellVersion(host?.info?.shell_version)
    : false;

  const PAYMENT_METHODS = getPaymentMethod(
    t,
    !clientUUID ||
      items.some((item) => item.type !== 'TARIFF') ||
      disableSellByDeposit,
    items.some(
      (item) =>
        item.type === EPaymentItemType.TARIFF &&
        item.original?.disable_sale_receipt_printing
    ) || hostIds.length > 1
  );
  useEffect(() => {
    setValue('cash_sum', '', { shouldValidate: true });
    setValue('card_sum', '', { shouldValidate: true });
  }, [method, total]);

  const cartItems = useMemo<ISendingPaymentItem[]>(
    () => getCartItems(items, combo, activeDiscount, selectedHostsIds),
    [items, combo, activeDiscount, hostIds, selectedHostsIds]
  );

  const hostGroupsTitlesById = useMemo(
    () =>
      bookingFormData?.hostGroupsOverview.reduce<Record<number, string>>(
        (acc, group) => {
          acc[group.id] = group.title;

          return acc;
        },
        {}
      ),
    [bookingFormData?.hostGroupsOverview]
  );

  const titleGroup = host && hostGroupsTitlesById?.[host.group_id];

  const timeBooking = getNearestBookingTime(
    bookingFormData?.hostsOverview,
    hostIds
  );

  const onSubmitForm: SubmitHandler<IFormFields> = ({
    clientUUID,
    discount,
    promoCode,
    method,
    card_sum,
  }) => {
    if (!method || items.length < 1 || isOnboardingPaymentLocked) {
      return;
    }

    if (method === 'DEPOSIT' && items.some((item) => item.type !== 'TARIFF')) {
      notification.info({
        t,
        type: 'warning',
        title: t('shop.deposit.notTariff'),
        dismiss: 3,
      });

      return;
    }
    const mergedShopItems =
      selectedHostsIds && selectedHostsIds.length > 1
        ? mergeTariffItems(cartItems, items)
        : items;
    const completesOnboardingSession =
      hasOnboardingTariff && method === EPaymentMethod.CASH;

    return createPayment({
      method,
      clientUUID,
      cardSum: card_sum || 0,
      promoCodeId: promoCode?.id,
      discountId: discount?.id,
      availableItems: availableItems,
      items: cartItems,
      shopItems: mergedShopItems,
      discountValue: activeDiscount,
    })
      .then((payment) => {
        if (isSandbox && completesOnboardingSession) {
          const paymentId = Number(
            (payment as { id?: number } | undefined)?.id
          );
          window.dispatchEvent(
            new CustomEvent<OnboardingSessionCreatedDetail>(
              ONBOARDING_SESSION_CREATED_EVENT,
              {
                detail: {
                  hostId,
                  paymentId: Number.isFinite(paymentId) ? paymentId : undefined,
                },
              }
            )
          );
        }
        onClose();
      })
      .catch(console.error);
  };

  return (
    <Modal onClose={onClose} className={s.container}>
      <header className={s.header}>
        {hostIds.length > 1
          ? t('startSeance.modal.title.more', titleGroup as string)
          : t('shop.modal.title', host?.alias ?? '')}
        {hostIds.length > 1 && timeBooking && (
          <Label text={t('startSeance.modal.label.more', timeBooking)} />
        )}
      </header>
      <form
        className={s['shop-form']}
        data-onboarding="start-session-sale"
        data-onboarding-tariff-selected={String(hasOnboardingTariff)}
        data-onboarding-cash-selected={String(method === EPaymentMethod.CASH)}
        onSubmit={handleSubmit(onSubmitForm)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();

            const target = e.target as HTMLElement;
            if (
              target.tagName === 'BUTTON' &&
              (target as HTMLButtonElement).type === 'submit'
            ) {
              handleSubmit(onSubmitForm)(e);
            }
          }
        }}
      >
        <div className={s['users-discount-col']}>
          {hostIds.length > 1 ? (
            <HostsField
              value={selectedHostsIds}
              onChange={setSelectedHostsIds}
              loading={isBookingFormDataLoading}
              hosts={bookingFormData?.hostsOverview.filter((host) =>
                hostIds.includes(host.id)
              )}
              hostGroupsTitlesById={hostGroupsTitlesById}
              currentBooking={bookingForEdit?.getBooking}
              tariffs
            />
          ) : (
            <Controller
              control={control}
              name="clientUUID"
              render={({ field: { value, onChange } }) => (
                <ClientSearch
                  chosenClientUUID={value}
                  setChosenClientUUID={onChange}
                  filterClientsFn={filterClientsFn}
                  className={s['client-field']}
                  disabled={
                    Boolean(host?.client_sessions[0] || host?.user) ||
                    (!permissions?.has('client_browse') &&
                      !permissions?.has('client_info'))
                  }
                  notShowButtonDeposit={false}
                  setIsDepositOpen={setIsDepositOpen}
                  setIsDefaultClient={setIsDefaultClient}
                  withReplenishment
                />
              )}
            />
          )}
          <Controller
            render={({ field: { value, onChange } }) => (
              <DiscountsField
                value={value}
                onChange={onChange}
                disabled={
                  Boolean(promoCode) ||
                  tab === 'COMBO' ||
                  !items.some((item) => item.use_global_discounts)
                }
              />
            )}
            name="discount"
            control={control}
          />
          {permissions?.has('promo_code_check') && (
            <Controller
              render={({ field: { value, onChange } }) => (
                <PromoCodeField
                  value={value}
                  onChange={onChange}
                  disabled={
                    Boolean(discount) ||
                    tab === 'COMBO' ||
                    !items.some((item) => item.use_global_discounts)
                  }
                  clientUUID={clientUUID}
                  items={items}
                  setAvailableItems={setAvailableItems}
                />
              )}
              name="promoCode"
              control={control}
            />
          )}
        </div>

        <div className={s['items-col']} data-onboarding="start-session-tariffs">
          {promoCode && (
            <div className={s.promo_disabled}>
              <H2>{t('shopForm.promo.disabled')}</H2>
              <span className={s.text}>
                {t('shopForm.promo.disabled.text')}
              </span>
            </div>
          )}
          <div className={s.controls}>
            {hostIds.length > 1 ? (
              <span className={s.tariffs_text}>{t('category.tariffs')}</span>
            ) : (
              <Tabs
                value={tab}
                options={TABS}
                onChange={setTab}
                className={s.tabs}
              />
            )}
            <Input
              variant="secondary"
              {...searchInputProps}
              placeholder={t('search.placeholder')}
              leftContent={<SearchIcon color="primary" />}
              errorWrapperClassName={s.search}
            />
          </div>
          <ShopItemsTable
            search={searchInputProps.value}
            onItemSelect={addItem}
            hostId={hostId}
            clientUUID={clientUUID ?? undefined}
            shift={tariffsDuration}
            setCombo={setCombo}
            combo={combo}
            disabledCombo={disabledCombo}
            disabledTariff={disabledTariff}
            items={items}
            isBookingLoading={isBookingFormDataLoading}
            promoCode={Boolean(promoCode?.id)}
          />
        </div>

        <div
          className={s['shopping-cart']}
          data-onboarding="start-session-cart"
        >
          <div className={s['shopping-info']}>
            <ShoppingCart
              combo={combo}
              setCombo={setCombo}
              items={items}
              discount={activeDiscount}
              setItemAmount={setItemAmount}
              removeItem={removeItem}
              availableItems={availableItems}
            />
            <div className={s.info}>
              <div className={cn(s.discount, s.description)}>
                <span>{t('discount')}</span>
                <span>
                  {activeDiscount &&
                  tab !== 'COMBO' &&
                  items.some((item) => item.use_global_discounts)
                    ? `${activeDiscount}%`
                    : '—'}
                </span>
              </div>
              <div className={cn(s.discount, s.description)}>
                <span>{t('shop.modal.end')}</span>
                <Timer
                  tariffsDuration={tariffsDuration}
                  shouldIncrement={
                    Boolean(items.length) &&
                    !items.some(
                      (item) =>
                        item.type === 'TARIFF' &&
                        item.original.has_fixed_finish_time
                    )
                  }
                />
              </div>
              <Separator />
            </div>
          </div>
          <div className={s.bottom} data-onboarding="start-session-payment">
            <Controller
              render={({ field: { value, onChange } }) => (
                <Tabs
                  options={PAYMENT_METHODS}
                  value={value}
                  onChange={onChange}
                />
              )}
              name="method"
              control={control}
              rules={{ required: true }}
            />
            {method === EPaymentMethod.CASH && (
              <CalculatorCash totalCash={total} />
            )}
            {method === EPaymentMethod.COMPOSITE && (
              <div className={s.composite}>
                <Controller
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <NumberInput
                      {...field}
                      onChange={(value) => {
                        if (value === '') {
                          field.onChange('');
                          setValue('card_sum', '');
                          return;
                        }

                        if (value.toString().split('.')[1]?.length > 2) {
                          return field.onChange(value.toFixed(2));
                        }

                        const formattedValue = parseFloat(value.toFixed(2));

                        field.onChange(formattedValue);

                        if (!total) {
                          return setValue('card_sum', '');
                        }

                        setValue(
                          'card_sum',
                          Number((total - formattedValue).toFixed(2))
                        );
                      }}
                      error={isTouched ? error?.message : undefined}
                      title={t('money.cash')}
                      rightContent={currency.sign}
                      variant="primary"
                      bordered
                    />
                  )}
                  control={control}
                  name="cash_sum"
                  rules={{
                    validate: (value) => {
                      if (method !== EPaymentMethod.COMPOSITE) return;

                      if (value === '') return t('errors.required');

                      if (value <= 0) {
                        return t('errors.min', 0.01, currency.sign);
                      }

                      if (value >= (total ?? 0)) {
                        return t('errors.split.minSum');
                      }
                    },
                  }}
                />
                <Controller
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <NumberInput
                      {...field}
                      onChange={(value) => {
                        if (value === '') {
                          field.onChange('');
                          setValue('card_sum', '');
                          return;
                        }

                        if (value.toString().split('.')[1]?.length > 2) {
                          return field.onChange(value.toFixed(2));
                        }

                        const formattedValue = parseFloat(value.toFixed(2));

                        field.onChange(formattedValue);

                        if (!total) {
                          return setValue('cash_sum', '');
                        }

                        setValue(
                          'cash_sum',
                          Number((total - formattedValue).toFixed(2))
                        );
                      }}
                      error={isTouched ? error?.message : undefined}
                      title={t('money.card')}
                      rightContent={currency.sign}
                      variant="primary"
                      bordered
                    />
                  )}
                  control={control}
                  name="card_sum"
                  rules={{
                    required: t('errors.required'),
                    validate: (value) => {
                      if (method !== EPaymentMethod.COMPOSITE) return;

                      if (value === '') return t('errors.required');

                      if (value <= 0) {
                        return t('errors.min', 0.01, currency.sign);
                      }

                      if (value >= (total ?? 0)) {
                        return t('errors.split.minSum');
                      }
                    },
                  }}
                />
              </div>
            )}
            <Button
              disabled={
                !isValid ||
                disabledButton ||
                isRoleClient === 'active_operator' ||
                selectedHostsIds?.length === 0 ||
                isOnboardingPaymentLocked
              }
              isLoading={isSubmitting}
              fullWidth
              type="submit"
            >
              <div className={s['submit-button']}>
                <span>{t('shoppingCart.submit')}</span>
                <span>
                  {total.toLocaleString()} {currency.sign}
                </span>
              </div>
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
